import LogoArSvg from '@assets/images/bloom-logo/logo-ar.svg';
import LogoSvg from '@assets/images/bloom-logo/logo.svg';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React, { useMemo } from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

/** Aspect from brand logo SVG viewBox (108 × 33). */
const ASPECT = 108 / 33;
/** Aspect from Arabic wordmark `red.svg` viewBox (496.06 × 176.34). */
const ARABIC_ASPECT = 496.06 / 176.34;

/**
 * Figma logo frame (Bloom Universal hub):
 * width 267.65 · height 91 · padding 6 / Spacing/4(16) / 10 / Spacing/4(16) · gap 10
 */
const FIGMA_FRAME_WIDTH = 267.65;
const FIGMA_FRAME_HEIGHT = 91;
const FIGMA_PAD_TOP = 6;
const FIGMA_PAD_BOTTOM = 10;
/** Spacing/4 in this design system = 16px */
const FIGMA_PAD_HORIZONTAL = 16;

export interface BloomWordmarkProps {
  /**
   * Scale vs Figma frame (1 = 267.65×91).
   * Phone can pass ~0.7 so it doesn’t dominate the stack.
   */
  readonly scale?: number;
  readonly style?: StyleProp<ViewStyle>;
}

/**
 * Bloom brand wordmark — same red logo as the app (`bloom-logo/logo.svg`).
 */
export function BloomWordmark({
  scale = 1,
  style,
}: Readonly<BloomWordmarkProps>) {
  const { isRTL } = useI18nContext();
  const layout = useMemo(() => {
    const frameWidth = FIGMA_FRAME_WIDTH * scale;
    const frameHeight = FIGMA_FRAME_HEIGHT * scale;
    const padTop = FIGMA_PAD_TOP * scale;
    const padBottom = FIGMA_PAD_BOTTOM * scale;
    const padH = FIGMA_PAD_HORIZONTAL * scale;
    const innerWidth = frameWidth - padH * 2;
    const innerHeight = frameHeight - padTop - padBottom;
    const byWidth = innerWidth / ASPECT;
    const svgHeight = Math.min(innerHeight, byWidth);
    const svgWidth = svgHeight * ASPECT;
    const arabicHeight = Math.min(innerHeight, innerWidth / ARABIC_ASPECT);
    const arabicWidth = arabicHeight * ARABIC_ASPECT;

    return {
      frameWidth,
      frameHeight,
      padTop,
      padBottom,
      padH,
      svgWidth,
      svgHeight,
      arabicWidth,
      arabicHeight,
    };
  }, [scale]);

  return (
    <View
      style={[
        styles.frame,
        {
          width: layout.frameWidth,
          height: layout.frameHeight,
          paddingTop: layout.padTop,
          paddingBottom: layout.padBottom,
          paddingHorizontal: layout.padH,
          opacity: 1,
        },
        style,
      ]}
      accessibilityRole="image"
      accessibilityLabel={isRTL ? 'بلووم' : 'Bloom'}
    >
      {isRTL ? (
        <LogoArSvg width={layout.arabicWidth} height={layout.arabicHeight} />
      ) : (
        <LogoSvg width={layout.svgWidth} height={layout.svgHeight} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  frame: {
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
});

export default BloomWordmark;
